/**
 * Page guide content shown by the floating info button on dashboard pages.
 *
 * Each entry explains one page to the person looking at it: why the page exists,
 * what it can do, the order things are meant to happen in, and the caveats that
 * are easy to trip over.
 *
 * Keys are route paths exactly as they appear in App.js, including aliases, so a
 * lookup is a direct hit with no pattern matching. Routes absent from this map
 * simply render no info button.
 */

const memberGuides = {
  "/user/dashboard": {
    audience: "member",
    title: "Dashboard",
    subtitle: "Your daily starting point.",
    purpose:
      "Brings together everything that needs your attention today - your streak, the next live class, videos you have started, and how your membership is doing - so you can begin practising without hunting through the menu.",
    capabilities: [
      "See your current practice streak and wellness snapshot",
      "Jump straight into the next live session or a video you left unfinished",
      "Glance at your active goals and how far along they are",
      "Check how many days are left on your membership",
    ],
    workflow: [
      "The page loads your profile, streak and today's check-in status automatically.",
      "If you have not checked in yet today, the check-in prompt appears first - complete it so today counts.",
      "Pick an activity from the cards: a live session, a practice video, or a goal to log.",
      "Come back later and the same cards reflect your updated progress.",
    ],
    notes: [
      "Cards for features your plan does not include will take you to an upgrade screen instead of the feature.",
      "Streak and wellness figures are recalculated from your attendance, so they update after you check in rather than instantly on page load.",
    ],
  },

  "/user/journey": {
    audience: "member",
    title: "My Journey",
    subtitle: "How your practice has changed over time.",
    purpose:
      "Turns your day-to-day activity into trends, so you can see whether your consistency, minutes practised and wellness markers are moving in the right direction over weeks and months rather than just today.",
    capabilities: [
      "Review progress charts across your whole membership",
      "Compare consistency between weeks and months",
      "See mindfulness minutes accumulated from videos and live classes",
      "Spot gaps where your practice dropped off",
    ],
    workflow: [
      "Open the page and your history is pulled from your attendance and activity records.",
      "Use the range controls to move between shorter and longer time windows.",
      "Read the trend rather than a single day - a missed day matters far less than a falling curve.",
      "Use what you see to adjust the targets on your Goals page.",
    ],
    notes: [
      "This page needs the Journey Analytics feature on your plan. If it is not enabled you will see an upgrade screen.",
      "Charts are built from logged activity only. Practice you never checked in for cannot appear here.",
    ],
  },

  "/user/traininglibrary": {
    audience: "member",
    title: "Training Library",
    subtitle: "On-demand practice videos.",
    purpose:
      "Your self-paced library. Everything here can be practised whenever you like, with your position remembered so a long programme can be worked through across several sittings.",
    capabilities: [
      "Browse and filter videos by category such as Asana, Pranayama, Meditation and Breathwork",
      "Resume a video from where you stopped",
      "Bookmark videos to build your own shortlist",
      "Keep private notes against a video",
    ],
    workflow: [
      "Filter or search to find a practice that suits the time you have.",
      "Open a video - locked ones show a tier badge instead of playing.",
      "Practise. Your progress is saved as you watch, so you can close the page safely.",
      "Bookmark anything you want to repeat; it stays in your shortlist.",
    ],
    notes: [
      "Each video carries its own tier. Your plan unlocks its own level and everything below it, so a higher plan widens the library rather than replacing it.",
      "Locked videos are deliberately listed rather than hidden, so you can see what a higher plan would add.",
      "Notes are kept on the device you wrote them on and are not synced between devices.",
    ],
  },

  "/user/livesession": {
    audience: "member",
    title: "Live Sessions",
    subtitle: "Scheduled classes with a trainer.",
    purpose:
      "Where you book and join classes that run at a fixed time with a live trainer. Sessions move through upcoming, live and ended on their own, based on their scheduled start and duration.",
    capabilities: [
      "See which sessions are upcoming, running right now, or finished",
      "Book a place in a session",
      "Cancel a booking or move it to a different session",
      "Watch the recording of a session you missed, where one has been published",
    ],
    workflow: [
      "Browse the list - each session shows its category, trainer, start time and whether it is open to your plan.",
      "Book the session you want. Booking is checked against both your plan and the remaining seats.",
      "When the start time arrives the session turns live and a join control appears.",
      "Join the room to practise. Your attendance is recorded and counts toward your streak and mindfulness minutes.",
    ],
    notes: [
      "Sessions are tier-gated. If your plan is below a session's level, booking is refused with an explanation.",
      "Seats are limited, so a session can fill up even when your plan allows it.",
      "Joining a live class also marks your attendance for that day - you do not need to check in separately.",
      "Recordings appear only after a trainer publishes one, and publishing can close a session earlier than its scheduled end.",
    ],
  },

  "/user/attendance": {
    audience: "member",
    title: "Attendance",
    subtitle: "Your daily practice record.",
    purpose:
      "The record of every day you showed up. This is what your streak, your consistency figures and several achievement badges are calculated from, so keeping it accurate matters.",
    capabilities: [
      "Check in and check out for today",
      "Review your full check-in history",
      "See your current streak and your longest run",
    ],
    workflow: [
      "Open the page - today's status is loaded first, so you can see whether you have already checked in.",
      "Tap check-in when you begin practising.",
      "Tap check-out when you finish, so the duration is recorded rather than just the day.",
      "Your streak updates from the saved record and feeds into Achievements and My Journey.",
    ],
    notes: [
      "Days are counted in Indian Standard Time, so a session that runs past midnight belongs to the new day.",
      "Live class attendance is written here automatically when you join a session.",
      "This page needs the Attendance Logs feature on your plan.",
    ],
  },

  "/user/dietplan": {
    audience: "member",
    title: "Diet Plans",
    subtitle: "Your weekly eating guidance.",
    purpose:
      "Pairs your practice with food. You are shown a seven-day plan broken into meal slots, matched to your stated preferences and to the plan you are on, and you tick meals off as you follow them.",
    capabilities: [
      "Record your diet preferences - diet type, region, calorie goal, allergies and activity level",
      "Follow a seven-day plan laid out by day and meal slot",
      "Tick off meals to track how closely you are following it",
      "Generate a personalised plan with AI, where your plan allows it",
    ],
    workflow: [
      "Set your preferences first - the plan you are shown is chosen to match them.",
      "Read through the week so you know what to shop for.",
      "Tick each meal off as you eat it; your adherence builds up over the week.",
      "Revisit your preferences whenever your goals or the season change, and the plan follows.",
    ],
    notes: [
      "The Customise Preferences button appears only when your plan enables it.",
      "AI plan generation is a separate switch again, and it has a monthly cap. Once the cap is reached you are asked to wait or upgrade.",
      "If no published plan fits your preferences exactly, you are given the closest available match for your tier.",
    ],
  },

  "/user/goals": {
    audience: "member",
    title: "Goals",
    subtitle: "Targets you set for yourself.",
    purpose:
      "Lets you commit to something specific and measurable - sessions per week, minutes of meditation, litres of water - and then log against it, so intention turns into a number you can see.",
    capabilities: [
      "Create goals across practice, mindfulness, wellness and nutrition",
      "Set your own target value and unit",
      "Log progress as you go and watch completion build",
      "Edit or remove a goal that no longer fits",
    ],
    workflow: [
      "Create a goal, choosing its category, target and unit.",
      "Log progress each time you do the thing - logs add up toward the target.",
      "Hitting a target triggers an achievement notification.",
      "Review at the end of the month and set the next one slightly higher.",
    ],
    notes: [
      "Some goals are kept in step with your recorded activity automatically, so practice you have already logged does not need entering twice.",
      "This page needs the Personal Goals feature on your plan.",
    ],
  },

  "/user/achievements": {
    audience: "member",
    title: "Achievements",
    subtitle: "Badges, certificates and standings.",
    purpose:
      "Recognises the milestones you have already passed. Everything here is earned from activity you have logged elsewhere - it is a read-out, not something you fill in.",
    capabilities: [
      "See the badges you have unlocked and what is still ahead",
      "View certificates awarded for completed programmes",
      "See where you stand relative to other members",
    ],
    workflow: [
      "The page recalculates your milestones from your attendance, video completions and goals each time you open it.",
      "Newly earned badges raise a notification so you do not miss them.",
      "Unearned badges show their criteria, so you know what to aim at next.",
    ],
    notes: [
      "Badges depend on logged activity. Practice that was never checked in cannot count toward them.",
      "This page needs the Achievements feature on your plan.",
    ],
  },

  "/user/notification": {
    audience: "member",
    title: "Notifications",
    subtitle: "Announcements and reminders.",
    purpose:
      "One place for everything the studio has sent you - class reminders, new video and diet plan announcements, and milestones you have hit - so nothing important is only ever an email.",
    capabilities: [
      "Read announcements sent to you or to your membership tier",
      "Mark items read individually or all at once",
      "Dismiss items you have dealt with, or clear the list",
      "Turn on browser push so reminders reach you outside the site",
    ],
    workflow: [
      "Open the page and unread items are shown first.",
      "Read what is relevant; the unread counter in the top bar clears as you go.",
      "Dismiss or clear anything you have finished with.",
      "Fine-tune which kinds of notification you receive from the Settings page.",
    ],
    notes: [
      "Reminders for live classes are raised about an hour before the session starts.",
      "Notifications are removed automatically after a retention period, so this is not a permanent archive.",
      "Turning categories off in Settings filters this list as well as push messages.",
    ],
  },

  "/user/profile": {
    audience: "member",
    title: "Profile",
    subtitle: "Your details and your membership.",
    purpose:
      "Holds who you are and what you are subscribed to. It is also where every renewal and upgrade happens - the upgrade prompts elsewhere in the app all lead back to the subscription tab here.",
    capabilities: [
      "Update your name, contact details and profile photo",
      "See your current plan, what it includes, and how many days remain",
      "Renew, upgrade or change your plan",
    ],
    workflow: [
      "Review your details and correct anything out of date.",
      "Open the subscription tab to see your current plan and its end date.",
      "Choose a plan to renew or move up to, and confirm.",
      "Access changes take effect immediately - locked pages open up as soon as the new plan is active.",
    ],
    notes: [
      "Moving to a new plan replaces your current one rather than running alongside it.",
      "A plan stays usable for a short grace period past its end date before features close off.",
      "Changing your email or phone requires values not already used by another member.",
    ],
  },

  "/user/settings": {
    audience: "member",
    title: "Settings",
    subtitle: "How the app behaves for you.",
    purpose:
      "Controls presentation, privacy, notifications and account security in one place. Display preferences here also govern how dates, currency and measurements are formatted everywhere else in the app.",
    capabilities: [
      "Set theme, language, timezone, date format, currency and measurement units",
      "Choose which categories of notification you receive",
      "Review the devices signed in to your account and sign them out",
      "Change your password, or deactivate or delete your account",
    ],
    workflow: [
      "Adjust display preferences - these save to your profile and follow you to other devices.",
      "Set your notification categories; switching one off hides it from your notification list too.",
      "Check the device list and revoke anything you do not recognise.",
      "Use the account controls at the bottom only when you intend to stop using the account.",
    ],
    notes: [
      "Changing your password signs your other devices out, which is intentional.",
      "Deactivating is reversible - you can reactivate later. Deleting is permanent and removes your history.",
      "Revoking a device ends its recorded session; that device may stay usable until its sign-in expires.",
    ],
  },
};

const adminGuides = {
  "/admin/dashboard": {
    audience: "admin",
    title: "Admin Dashboard",
    subtitle: "The health of the platform at a glance.",
    purpose:
      "The daily operating view. It answers whether members are turning up, whether revenue is holding, and whether anything needs intervening on today - and links straight through to the page where you would act.",
    capabilities: [
      "Read headline figures for members, active users, paid subscribers, revenue, content and check-ins",
      "Follow growth trends for registrations, uploads, sessions and revenue by month",
      "See alert cards for inactive members, expiring subscriptions and flagged accounts",
      "Search across members, content, sessions, diet plans and notifications from the top bar",
    ],
    workflow: [
      "Start with the headline tiles to see whether anything has moved sharply.",
      "Read the growth chart for direction rather than a single month's number.",
      "Work the alert cards - each one links to the page where the problem can be fixed.",
      "Use the search box when you need a specific member or item rather than an overview.",
    ],
    notes: [
      "Figures are computed live on each load, so heavy ranges take a moment to appear.",
      "Revenue is drawn from recorded subscriptions, so a subscription created without payment still counts toward it.",
    ],
  },

  "/admin/usermanagement": {
    audience: "admin",
    title: "User Management",
    subtitle: "The member register.",
    purpose:
      "The definitive list of members and the place to act on an individual account - creating it, correcting it, suspending it, or looking into how someone has actually been using the platform.",
    capabilities: [
      "Search and page through all members",
      "Create a member directly, with no email verification step",
      "Edit member details and check email, phone and username availability",
      "Activate or suspend an account, or delete it",
      "Inspect a member's sign-in sessions and revoke them",
    ],
    workflow: [
      "Find the member by search rather than scrolling.",
      "Open the record to see their details, plan and recent activity together.",
      "Make the change - edit details, toggle their status, or review their sessions.",
      "For anything to do with their plan, move to the Subscriptions page.",
    ],
    notes: [
      "Members created here skip email OTP verification, so confirm the address is correct before saving.",
      "Suspending is reversible and keeps history. Deleting removes the member and their related records.",
      "Staff accounts are not managed here - they live under Settings and are restricted to SuperAdmin.",
    ],
  },

  "/admin/subscription": {
    audience: "admin",
    title: "Subscriptions",
    subtitle: "Plans, pricing and who is on what.",
    purpose:
      "Defines what you sell and administers what members hold. This is the most consequential page in the back office - changing a package changes what every member on it can reach.",
    capabilities: [
      "Create and edit packages: name, description, price, discount, duration and badge",
      "Set a package's access level, which decides the tiered content it unlocks",
      "Switch individual features on or off per package, and cap AI diet generations",
      "Activate, deactivate or remove a package",
      "Assign, extend or cancel a member's subscription, and review the ledger",
    ],
    workflow: [
      "Open a package and set its commercial terms first - price, discount and duration.",
      "Set its access level next. This governs tiered content such as videos, live sessions and diet plans.",
      "Then use the feature switches for page-level access such as analytics, goals, attendance and the AI diet generator.",
      "Save, then verify against a test member that the right things unlocked.",
      "Use the subscription list to assign, extend or cancel individual members as needed.",
    ],
    notes: [
      "Access level and the feature switches are two separate mechanisms. Access level opens tiered content; the switches open specific pages. A package needs both set correctly.",
      "Access level is cumulative - a level unlocks everything at or below it.",
      "New packages start with every feature switch off. Enable them deliberately.",
      "The AI diet generator can also come on automatically at higher access levels, even with its switch left off.",
      "Editing a live package takes effect immediately for every member already on it.",
      "Deactivating a package hides it from new sign-ups but does not end existing subscriptions.",
    ],
  },

  "/admin/contentmanager": {
    audience: "admin",
    title: "Content Manager",
    subtitle: "Practice videos and written content.",
    purpose:
      "Where the on-demand library is built and maintained. Each video's tier decides which members can watch it, so tagging is as important as uploading.",
    capabilities: [
      "Upload practice videos, individually or in bulk",
      "Set category, tier, description and supporting details",
      "Edit or remove existing videos",
      "Manage blog and written content",
      "Review engagement figures such as views and completion",
    ],
    workflow: [
      "Upload the video and wait for it to finish transferring to storage.",
      "Set its category and, critically, its tier - this is what gates access.",
      "Save and check it appears correctly for the tiers you intended.",
      "Members on qualifying plans are notified that new content is available.",
    ],
    notes: [
      "Tier is the access control. A video left on a low tier is visible to more members than you may intend.",
      "Deleting a video removes the stored file as well as the record, and cannot be undone.",
      "Uploads are large. Leave the page open until the transfer completes.",
    ],
  },

  "/admin/livesession": {
    audience: "admin",
    title: "Live Sessions",
    subtitle: "Scheduling and running live classes.",
    purpose:
      "Controls the live timetable. A session's state is derived from its start time and duration rather than set by hand, so scheduling it accurately is what makes it behave correctly for members.",
    capabilities: [
      "Schedule sessions with trainer, category, start time, duration and capacity",
      "Set a session's tier and seat limit",
      "Edit or cancel a scheduled session",
      "Upload or attach a recording afterwards, and remove one",
      "See who booked and who attended",
    ],
    workflow: [
      "Create the session with its time, duration, tier and capacity.",
      "Members on qualifying plans are notified, and reminded again about an hour beforehand.",
      "The session becomes live on its own at the scheduled time - nothing needs starting manually.",
      "Afterwards, attach the recording so members who missed it can catch up.",
    ],
    notes: [
      "Live, upcoming and ended are calculated from the schedule. To end a class early, publish its recording - that shortens the session to the time already elapsed.",
      "Tier and capacity are both enforced at booking, so a member can be refused for either reason.",
      "Deleting a session also removes its bookings and attendance records.",
    ],
  },

  "/admin/dietplans": {
    audience: "admin",
    title: "Diet Plan Manager",
    subtitle: "Nutrition templates for members.",
    purpose:
      "Builds the library of seven-day plans that members are matched into. A member is served a plan whose tier and attributes fit them, so coverage across the combinations you support is what matters here.",
    capabilities: [
      "Create plans with meals laid out by day and meal slot",
      "Classify a plan by tier, diet type, region and calorie goal",
      "Move plans between draft, published and archived",
      "Generate a plan with AI as a starting point",
      "Track how closely members are following their plans",
      "Clear duplicates",
    ],
    workflow: [
      "Create the plan and classify it - tier, diet type, region and calorie goal decide who it reaches.",
      "Fill in meals for each day and slot, or generate a draft with AI and edit it.",
      "Publish when it is complete. Drafts are not served to members.",
      "Use the tracking view to see whether members are actually following it.",
    ],
    notes: [
      "Only published plans reach members. A draft is invisible to them.",
      "Members are matched to the closest fitting published plan for their tier, so gaps in coverage mean someone gets an imperfect match.",
      "AI output is a draft. Review it before publishing.",
    ],
  },

  "/admin/highpriorityuser": {
    audience: "admin",
    title: "High Priority Users",
    subtitle: "Members who need attention now.",
    purpose:
      "A worklist rather than a report. It surfaces the members most at risk of lapsing - subscriptions about to expire, payments that failed, accounts that have gone quiet, health flags - so retention work has a queue.",
    capabilities: [
      "See subscriptions expiring soon",
      "See failed or pending payments",
      "See members who have stopped practising",
      "See accounts carrying health flags",
      "Open any member directly to act on them",
    ],
    workflow: [
      "Work the list from the most urgent group down.",
      "Open a member to see their history and understand why they are flagged.",
      "Act - extend or fix their subscription, or send them a notification.",
      "The member drops off the list once the underlying condition clears.",
    ],
    notes: [
      "Entries are derived live from subscription and activity data, so the list changes as members act.",
      "A member can appear for more than one reason at once.",
    ],
  },

  "/admin/reports&analytics": {
    audience: "admin",
    title: "Reports & Analytics",
    subtitle: "Trends over a chosen period.",
    purpose:
      "The analytical counterpart to the dashboard. Where the dashboard shows today, this shows movement across a window you choose, for judging whether things are actually improving.",
    capabilities: [
      "Choose a reporting window and compare periods",
      "Review revenue and subscription trends",
      "Review engagement across content and sessions",
      "Export figures for reporting",
    ],
    workflow: [
      "Pick the window you want to examine.",
      "Read revenue and subscription movement together - growth in one without the other usually needs explaining.",
      "Cross-check engagement to see whether members are using what they pay for.",
      "Export when you need the numbers outside the platform.",
    ],
    notes: [
      "Longer windows aggregate by month and take longer to compute.",
      "Figures come from recorded subscriptions rather than a payment provider, so they reflect what is in the system.",
    ],
  },

  "/admin/notifications": {
    audience: "admin",
    title: "Notifications",
    subtitle: "Reaching members directly.",
    purpose:
      "Used to announce and remind - new content, schedule changes, offers. Messages can go to everyone, to particular membership tiers, or to one member, and you can check afterwards who actually received them.",
    capabilities: [
      "Compose and send a notification",
      "Target everyone, specific tiers, or an individual member",
      "Review everything sent previously",
      "Open a delivery report showing recipients",
      "Remove notifications that are no longer relevant",
    ],
    workflow: [
      "Write the message and choose who it goes to.",
      "Check the audience before sending - a tier-targeted message will not reach members outside it.",
      "Send. It appears in members' notification lists and goes out as browser push to anyone subscribed.",
      "Open the delivery report to confirm it landed.",
    ],
    notes: [
      "Members can switch notification categories off, and that filters what you send to them.",
      "Browser push only reaches members who have granted permission; the in-app notification always lands.",
      "Notifications are cleaned up automatically after a retention period.",
      "Announcements for new videos, diet plans and live sessions are sent automatically when you publish - no need to send those by hand.",
    ],
  },

  "/admin/settings": {
    audience: "admin",
    title: "Settings",
    subtitle: "Branding and platform configuration.",
    purpose:
      "Controls how the platform presents itself and, for SuperAdmin, who can administer it. Changes here are global and visible to every visitor and member immediately.",
    capabilities: [
      "Set branding such as logo and favicon",
      "Upload and set the ambient background music used on the public site",
      "Adjust appearance and system parameters",
      "Manage staff accounts and their roles, where your role permits it",
    ],
    workflow: [
      "Change the setting you need - most take effect for everyone as soon as they are saved.",
      "Check the public site afterwards to confirm branding looks right.",
      "For staff accounts, create the account and assign its role deliberately.",
    ],
    notes: [
      "Creating and managing staff accounts is restricted to SuperAdmin. Admin and Trainer roles cannot see those controls.",
      "Branding and audio changes are global and apply immediately, including for signed-out visitors.",
      "Some appearance settings are cached in the browser, so a hard refresh may be needed to see them change.",
    ],
  },

  "/admin/queries": {
    audience: "admin",
    title: "User Queries",
    subtitle: "Inquiries, support requests and correspondence.",
    purpose:
      "Your central support inbox. Collects every message submitted through public contact forms and member support requests, allowing administrators to triage, review, add internal resolution notes, and reply directly via email.",
    capabilities: [
      "Filter inquiries by lifecycle status: Pending, In Review, Resolved, and Archived",
      "Categorise by topic such as Class Bookings, Subscriptions & Billing, Teacher Training, and Technical Support",
      "Read full inquiry messages, contact phone numbers, and submission timestamps",
      "Send direct email replies via SMTP with customizable response templates",
      "Attach private internal admin notes to track correspondence and phone calls",
      "Permanently delete resolved or spam inquiries with authorization confirmation",
    ],
    workflow: [
      "Start with the headline metric tiles to see how many inquiries need attention today.",
      "Filter by status or topic to find messages requiring immediate response.",
      "Click on any inquiry row to open the full inspection modal and mark it as read.",
      "Use 'Reply via Email' to send an official response; the inquiry status can automatically update to Resolved upon sending.",
      "Save internal resolution notes so other administrators can see what actions were taken.",
    ],
    notes: [
      "Replying via email sends directly through the configured SMTP service and updates the user immediately.",
      "Inquiries left in 'Pending' contribute to the notification badge on the sidebar until addressed.",
      "Deleting an inquiry is permanent and removes the communication record from the database.",
      "Status changes take effect immediately and recalculate the headline breakdown counters.",
    ],
  },
};

/** Alias route -> canonical route, mirroring the duplicate paths declared in App.js. */
const routeAliases = {
  "/admin/users": "/admin/usermanagement",
  "/admin/subscriptions": "/admin/subscription",
  "/admin/livesessions": "/admin/livesession",
  "/admin/highpriorityusers": "/admin/highpriorityuser",
  "/admin/high-priority-users": "/admin/highpriorityuser",
  "/admin/priority": "/admin/highpriorityuser",
  "/admin/user-queries": "/admin/queries",
  "/user/setting": "/user/settings",
};

const pageGuides = { ...memberGuides, ...adminGuides };

/**
 * Resolve the guide for a pathname, or null when the page has no guide.
 */
export function getPageGuide(pathname) {
  if (!pathname) return null;

  let key = pathname.toLowerCase();
  if (key.length > 1) key = key.replace(/\/+$/, "");

  return pageGuides[routeAliases[key] || key] || null;
}

export default pageGuides;
